// ═════════════════════════════════════════════════════════
// AMSELWEG 14B — Potsdam-Marquardt Dealroom
// Mirrors the Off Market Karlshorst act-by-act narrative:
//   Act I  Hero            (cinematic parallax, project headline)
//   Act II Macro Case      (set the context — Potsdam research, why now)
//   Act III Asset Details  (gallery, snapshot, floor plan)
//   Act IV Calculator      (story / detailed views — reuses ProjectsPage CalculatorAct)
//   Act V  FAQ / Objections
//   Act VI Closing CTA
// Uses Lenis smooth scroll + scroll HUD + section anchor nav, same as Karls.
// ═════════════════════════════════════════════════════════

import { useEffect, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight, MapPin, Calendar, Home, Zap, Building2, TrendingUp,
  Award, Shield, Sparkles, Trees, Train, Users, Briefcase, CheckCircle2, Maximize2,
} from "lucide-react";
import Lenis from "@studio-freight/lenis";
import { IS_MOBILE } from "@/lib/isMobile";
import {
  CalculatorAct,
  Reveal,
  ScrollProgressHUD,
  AmbientCursorGlow,
  FaqObjections,
} from "@/components/ProjectsPage";
import {
  AMSELWEG_DEFAULTS,
  AMSELWEG_PROFILES,
  AMSELWEG_SCENARIOS,
} from "@/lib/amselwegCalc";
import { openCalendly } from "@/lib/calendly";
import { useNavigate } from "react-router-dom";
import { Lightbox, useLightbox } from "@/components/Lightbox";
import { webpSrc } from "@/lib/webpSrc";
import Img from "@/components/Img";
import HeroPreload from "@/components/HeroPreload";
import { useLang } from "@/lib/i18n";

// ═════════════════════════════════════════════════════════
// COPY — EN + DE (English default per site-wide language)
// ═════════════════════════════════════════════════════════
const COPY = {
  en: {
    hero: {
      eyebrow: "Private Dealroom · Potsdam-Marquardt",
      title: "Amselweg 14B",
      sub: "The last newbuild house in the Potsdam corridor.",
      body: "124.5 m² of A+ newbuild quality. €100,000 KfW funding block. 5% degressive AfA. A real lettings track-record. Q2 2026 handover. This is the editorial investment case — the macro thesis, the asset, and the math.",
      chips: [
        { v: "124.5 m²", l: "Living area" },
        { v: "€580k",    l: "Total entry" },
        { v: "Q2 2026",  l: "Handover" },
        { v: "A+ · KfW", l: "Energy · funding" },
      ],
      scroll: "Read the case",
    },
    macro: {
      act: "Act I",
      kicker: "Set the context",
      headingA: "Why Potsdam.",
      headingB: "Why now. Why this corridor.",
      body: "Before the unit. Before the math. Read the macro thesis. Potsdam is the second-strongest housing market in eastern Germany, structurally undersupplied, and Marquardt sits inside the Potsdam/Berlin functional corridor on the RB21 line. The combination is what makes the price defensible.",
      stats: [
        { v: "176,000+", l: "Potsdam population",   s: "+9% since 2015 · Brandenburg's economic engine" },
        { v: "+2.4%",    l: "Annual rent growth",   s: "Potsdam metro · 5-year average to 2025" },
        { v: "~3,300",   l: "Net job growth p.a.",  s: "Science · government · Babelsberg media cluster" },
        { v: "<2.5%",    l: "Rental vacancy",       s: "Potsdam corridor · structural undersupply" },
      ],
      layers: [
        { tag: "Layer 1", icon: Building2, title: "Potsdam as a core market",         body: "Brandenburg's capital, seat of government, science city, Babelsberg media cluster. Among Germany's strongest housing markets by net migration, income and scarcity. Demand is structural — not cycle-dependent." },
        { tag: "Layer 2", icon: Trees,     title: "Marquardt's green micro-market",   body: "Space, garden character, water proximity, family-grade housing. Central Potsdam cannot deliver this €/m² at this product spec. Marquardt offers the lifestyle that priced-out Berliners are migrating to." },
        { tag: "Layer 3", icon: Train,     title: "RB21 + Friedrichspark upside",     body: "RB21 anchors functional access to Potsdam and Berlin. The Friedrichspark / Marquardt commercial development is an upside catalyst — not a base case. The thesis works without it." },
      ],
      quoteA: "The base case is Potsdam strength + green living quality.",
      quoteB: "The upside case is the Friedrichspark catalyst.",
      quoteSub: "Marquardt isn't a village trade. It's a corridor trade. The product/quality/€ ratio cannot be re-issued at central Potsdam pricing.",
    },
    asset: {
      act: "Act II",
      kicker: "Meet the asset",
      headingA: "One unit left.",
      headingB: "Inspectable. Photographable. Real.",
      body: "Not a render-only product. Four units already let, one sold via IRE — Amselweg 14B is the last. Click any image to expand.",
      soldLabel: "Sold via IRE",
      letLabel: "Let",
      availLabel: "Available · 14B",
      unitLbl: "Unit",
      dealSnapshotKicker: "Deal snapshot",
      dealSnapshotHeading: "The hard numbers",
      specHighlights: "Specification highlights",
      siteProofKicker: "Site evidence · 2026 build progress",
      clickExpand: "Click to expand",
      snapshotRows: [
        { l: "Address",       v: "Amselweg 14B, 14476 Potsdam-Marquardt" },
        { l: "Asset type",    v: "Newbuild semi-detached house" },
        { l: "Living area",   v: "124.5 m²", emphasis: true },
        { l: "Year built",    v: "2026 · A+ energy class · heat pump" },
        { l: "Total price",   v: "€580,000", emphasis: true },
        { l: "Property only", v: "€564,000 · €4,530/m²" },
        { l: "Kitchen / furniture", v: "€16,000 · move-in ready" },
        { l: "Cold rent",     v: "€1,992 / mo · €16.00/m²" },
        { l: "Gross yield",   v: "≈ 4.12 %", emphasis: true },
        { l: "Main loan",     v: "€480,000 @ 4.72 % · 1.50 % repayment" },
        { l: "KfW funding",   v: "€100,000 @ 2.30 % · Y1–5 interest only", emphasis: true },
        { l: "Modelled growth", v: "3.0 % p.a. · base case" },
        { l: "Availability",  v: "Last unit · 5 already let/sold", emphasis: true },
      ],
      features: [
        "Newbuild 2026 · QNG-eligible",
        "Energy class A+ · heat pump",
        "Designer kitchen included (€16k)",
        "High-spec bathrooms + guest WC",
        "Family-grade plan · 124.5 m²",
        "Private garden / outdoor area",
      ],
      galleryLabels: [
        "Garden · firepit patio at golden hour",
        "Kitchen · designer fit-out · leather barstools",
        "Family bathroom · attic skylight",
        "Bedroom · attic floor · field view",
        "Interior staircase · red handrail",
        "Front entrance · numbered '18'",
        "Completed house · garden + dev context",
        "Street view · row of completed houses",
        "Façade close-up · A+ envelope",
        "Kitchen detail · cabinetry",
        "Bathroom · green-tile compact",
        "Bedroom · skylight detail",
      ],
      siteLabels: [
        "Site overview · 2026",
        "Scaffolding phase · cluster",
        "Completed neighbours · cluster",
        "Façade close-up · build progress",
      ],
    },
    frame: {
      kicker: "Frame shift",
      side:   "Don't value it like a rental",
      headingA: "A 50\u2011m² rental compresses back to investors.",
      headingB: "A 125\u2011m² newbuild house also fits a family.",
      cards: [
        { icon: Users, h: "Two exit markets, not one",       p: "Owner-occupiers don't price by yield. They price by space, garden, energy, schools. That second buyer can re-rate the asset." },
        { icon: Briefcase, h: "Structural tax architecture", p: "5% degressive AfA + €100k KfW + interest deductibility = the tail of the math, not the headline of the rent." },
        { icon: Shield, h: "Newbuild risk filter",           p: "A+ envelope and heat pump. No fossil-heating regulation hit, no refurb-shock capex, best lettability vs. older stock." },
      ],
    },
    closing: {
      kicker: "The next step",
      headingA: "Ready to move on",
      headingB: "Amselweg 14B?",
      body: "One unit. Q2 2026 handover. The financing window for KfW EH55 is time-bound. Book a 30-minute strategy call — we confirm fit, prepare your financing review and walk you through every line of the model.",
      steps: [
        { n: "01", title: "Strategy call",         body: "30 minutes, no obligation. We confirm fit, your bracket, and your financing structure." },
        { n: "02", title: "Financing prep",        body: "We coordinate bank and KfW review in parallel, so timing matches the Q2 2026 handover window." },
        { n: "03", title: "Reservation + notary",  body: "Non-binding reservation, then notary closing. Typical timeline 4–8 weeks from go-ahead." },
      ],
      primaryCta: "Book a strategy call",
      secondaryCta: "Or start the qualification →",
      disclaimer: "This page is a marketing summary prepared by IRE Berlin. It does not constitute investment, legal or tax advice. Modelled values are not guaranteed. KfW funding is subject to bank approval and the EH55 funding window.",
    },
    calc: {
      act: "Act III",
      kicker: "Step into the math",
      headA: "Two phases.",
      headB: "One transparent set of numbers.",
    },
  },
  de: {
    hero: {
      eyebrow: "Privater Dealroom · Potsdam-Marquardt",
      title: "Amselweg 14B",
      sub: "Das letzte Neubauhaus im Potsdamer Korridor.",
      body: "124.5 m² Neubau-Qualität in A+. €100,000 KfW-Förderpaket. 5 % degressive AfA. Reale Vermietungs-Historie. Übergabe Q2 2026. Das ist der redaktionelle Investment-Case — Makro-These, Objekt, Rechnung.",
      chips: [
        { v: "124.5 m²", l: "Wohnfläche" },
        { v: "€580k",    l: "Gesamteinstieg" },
        { v: "Q2 2026",  l: "Übergabe" },
        { v: "A+ · KfW", l: "Energie · Förderung" },
      ],
      scroll: "Case lesen",
    },
    macro: {
      act: "Akt I",
      kicker: "Kontext setzen",
      headingA: "Warum Potsdam.",
      headingB: "Warum jetzt. Warum dieser Korridor.",
      body: "Vor dem Objekt. Vor der Rechnung. Lesen Sie die Makro-These. Potsdam ist der zweitstärkste Wohnungsmarkt in Ostdeutschland, strukturell unterversorgt, und Marquardt liegt im funktionalen Potsdam/Berlin-Korridor an der RB21. Genau diese Kombination macht den Preis verteidigungsfähig.",
      stats: [
        { v: "176,000+", l: "Einwohner Potsdam",       s: "+9% seit 2015 · Brandenburgs Wirtschaftsmotor" },
        { v: "+2.4%",    l: "Mietwachstum p.a.",       s: "Potsdam Metro · 5-Jahres-Schnitt bis 2025" },
        { v: "~3,300",   l: "Netto-Jobwachstum p.a.",  s: "Wissenschaft · Regierung · Babelsberg-Cluster" },
        { v: "<2.5%",    l: "Miet-Leerstand",          s: "Potsdamer Korridor · strukturelle Unterversorgung" },
      ],
      layers: [
        { tag: "Ebene 1", icon: Building2, title: "Potsdam als Kernmarkt",              body: "Landeshauptstadt Brandenburgs, Regierungssitz, Wissenschaftsstadt, Babelsberg-Cluster. Zu Deutschlands stärksten Wohnungsmärkten nach Zuzug, Einkommen und Knappheit. Nachfrage ist strukturell — nicht zyklisch." },
        { tag: "Ebene 2", icon: Trees,     title: "Marquardts grüner Mikromarkt",       body: "Fläche, Gartencharakter, Wassernähe, familiengerechter Wohnraum. Potsdam-Zentrum kann diesen €/m² bei dieser Spezifikation nicht liefern. Marquardt bietet den Lifestyle, in den verdrängte Berliner umziehen." },
        { tag: "Ebene 3", icon: Train,     title: "RB21 + Friedrichspark-Upside",        body: "Die RB21 sichert die funktionale Anbindung an Potsdam und Berlin. Das Friedrichspark/Marquardt-Gewerbequartier ist ein Upside-Katalysator — kein Basisszenario. Die These trägt auch ohne." },
      ],
      quoteA: "Die Basis: Potsdam-Stärke + grüne Wohnqualität.",
      quoteB: "Der Upside: der Friedrichspark-Katalysator.",
      quoteSub: "Marquardt ist kein Dorf-Trade. Es ist ein Korridor-Trade. Das Verhältnis Produkt/Qualität/€ ist bei Potsdam-Zentrum-Preisen nicht wiederholbar.",
    },
    asset: {
      act: "Akt II",
      kicker: "Das Objekt",
      headingA: "Eine Einheit bleibt.",
      headingB: "Besichtigbar. Fotografierbar. Real.",
      body: "Kein Rendering-Produkt. Vier Einheiten bereits vermietet, eine über IRE verkauft — Amselweg 14B ist die letzte. Jedes Bild lässt sich vergrößern.",
      soldLabel: "Verkauft via IRE",
      letLabel: "Vermietet",
      availLabel: "Verfügbar · 14B",
      unitLbl: "Einheit",
      dealSnapshotKicker: "Deal-Snapshot",
      dealSnapshotHeading: "Die harten Zahlen",
      specHighlights: "Ausstattungs-Highlights",
      siteProofKicker: "Bau-Nachweis · Baufortschritt 2026",
      clickExpand: "Zum Vergrößern klicken",
      snapshotRows: [
        { l: "Adresse",              v: "Amselweg 14B, 14476 Potsdam-Marquardt" },
        { l: "Objekttyp",            v: "Neubau-Doppelhaushälfte" },
        { l: "Wohnfläche",           v: "124.5 m²", emphasis: true },
        { l: "Baujahr",              v: "2026 · Energieklasse A+ · Wärmepumpe" },
        { l: "Gesamtpreis",          v: "€580,000", emphasis: true },
        { l: "Immobilie only",       v: "€564,000 · €4,530/m²" },
        { l: "Küche / Möblierung",   v: "€16,000 · schlüsselfertig" },
        { l: "Kaltmiete",            v: "€1,992 p.M. · €16.00/m²" },
        { l: "Bruttorendite",        v: "≈ 4.12 %", emphasis: true },
        { l: "Hauptdarlehen",        v: "€480,000 @ 4.72 % · 1.50 % Tilgung" },
        { l: "KfW-Förderung",        v: "€100,000 @ 2.30 % · Y1–5 tilgungsfrei", emphasis: true },
        { l: "Modellwachstum",       v: "3.0 % p.a. · Basisszenario" },
        { l: "Verfügbarkeit",        v: "Letzte Einheit · 5 bereits vermietet/verkauft", emphasis: true },
      ],
      features: [
        "Neubau 2026 · QNG-fähig",
        "Energieklasse A+ · Wärmepumpe",
        "Designer-Küche inklusive (€16k)",
        "Hochwertige Bäder + Gäste-WC",
        "Familienfreundlicher Grundriss · 124.5 m²",
        "Privatgarten / Außenbereich",
      ],
      galleryLabels: [
        "Garten · Feuerstelle im goldenen Licht",
        "Küche · Designer-Ausbau · Lederhocker",
        "Familienbad · Dachfenster",
        "Schlafzimmer · Dachgeschoss · Feldblick",
        "Innentreppe · roter Handlauf",
        "Eingang · Hausnummer '18'",
        "Fertiggestelltes Haus · Garten + Umgebung",
        "Straßenansicht · Reihe fertiger Häuser",
        "Fassade nah · A+ Hülle",
        "Küche · Möbeldetail",
        "Bad · Grünkachel kompakt",
        "Schlafzimmer · Dachfenster-Detail",
      ],
      siteLabels: [
        "Baustellen-Übersicht · 2026",
        "Gerüst-Phase · Cluster",
        "Fertige Nachbarhäuser · Cluster",
        "Fassade nah · Baufortschritt",
      ],
    },
    frame: {
      kicker: "Perspektivwechsel",
      side:   "Nicht wie eine Mietwohnung bewerten",
      headingA: "Eine 50\u2011m²-Miete ist kompressiv für Investoren.",
      headingB: "Ein 125\u2011m²-Neubauhaus passt auch einer Familie.",
      cards: [
        { icon: Users, h: "Zwei Exit-Märkte, nicht einer",         p: "Eigennutzer preisen nicht nach Rendite. Sie preisen nach Fläche, Garten, Energie, Schulen. Dieser zweite Käufer kann den Wert neu ansetzen." },
        { icon: Briefcase, h: "Strukturelle Steuer-Architektur",    p: "5 % degressive AfA + €100k KfW + Zinsabzug = das Ende der Rechnung, nicht die Schlagzeile der Miete." },
        { icon: Shield, h: "Neubau als Risikofilter",               p: "A+ Hülle und Wärmepumpe. Kein Fossilheizungs-Regulierungs-Hit, keine Sanierungs-Kapex, beste Vermietbarkeit gegen Altbestand." },
      ],
    },
    closing: {
      kicker: "Der nächste Schritt",
      headingA: "Bereit weiterzugehen mit",
      headingB: "Amselweg 14B?",
      body: "Eine Einheit. Übergabe Q2 2026. Das Finanzierungsfenster für KfW EH55 ist zeitgebunden. Buchen Sie ein 30-min Strategiegespräch — wir bestätigen den Fit, bereiten die Finanzierungsprüfung vor und gehen jede Zeile des Modells mit Ihnen durch.",
      steps: [
        { n: "01", title: "Strategiegespräch",       body: "30 Minuten, unverbindlich. Wir prüfen Passung, Ihre Steuerklasse und die Finanzierungsstruktur." },
        { n: "02", title: "Finanzierungsvorbereitung", body: "Wir koordinieren Bank- und KfW-Prüfung parallel, damit das Timing zur Übergabe Q2 2026 passt." },
        { n: "03", title: "Reservierung + Notar",     body: "Unverbindliche Reservierung, dann notarieller Abschluss. Typische Timeline 4–8 Wochen ab Freigabe." },
      ],
      primaryCta: "Strategiegespräch buchen",
      secondaryCta: "Oder Qualifizierung starten →",
      disclaimer: "Diese Seite ist eine Marketing-Zusammenfassung von IRE Berlin. Sie stellt keine Anlage-, Rechts- oder Steuerberatung dar. Modellierte Werte sind nicht garantiert. Die KfW-Förderung unterliegt der Bankgenehmigung und dem EH55-Förderfenster.",
    },
    calc: {
      act: "Akt III",
      kicker: "In die Rechnung",
      headA: "Zwei Phasen.",
      headB: "Ein transparenter Zahlensatz.",
    },
  },
};

const useCopy = () => {
  const { lang } = useLang();
  return COPY[lang === "de" ? "de" : "en"];
};

// ═════════════════════════════════════════════════════════
// Real photos (extracted from the developer's WhatsApp brief PDF)
// Each image has a `thumb` (800w, ~70-180kb) for tiles and `full` (2000w, original) for lightbox.
// ═════════════════════════════════════════════════════════
const photo = (n, ext = "jpg") => ({
  thumb: webpSrc(`/assets/amselweg/img-${String(n).padStart(2, "0")}-thumb.${ext}`),
  full:  webpSrc(`/assets/amselweg/img-${String(n).padStart(2, "0")}.${ext}`),
});

const IMG = {
  hero:          photo(1),
  facade:        photo(2),
  exterior:      photo(3),
  kitchen:       photo(4),
  garden:        photo(5),
  bathAttic:     photo(6),
  kitchen2:      photo(7),
  streetscape:   photo(8),
  construction1: photo(9),
  construction2: photo(10),
  siteOverview:  photo(11),
  construction3: photo(12),
  construction4: photo(13),
  interiorRaw:   photo(14),
  streetView:    photo(15),
  staircase:     photo(16),
  completedView: photo(17),
  bathGreen:     photo(18),
  bath:          photo(19),
  bedroom:       photo(20),
  bedroom2:      photo(21),
};

// Responsive hero variants
const HERO_SRC = webpSrc("/assets/amselweg/img-01-1920.jpg");
const HERO_SRC_MOBILE = "/assets/amselweg/img-01-960.jpg";

// ═════════════════════════════════════════════════════════
// ACT I — HERO
// ═════════════════════════════════════════════════════════
const Hero = () => {
  const T = useCopy().hero;
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  // Parallax disabled on mobile — compositor thrash was causing phone hangs.
  const bgY            = useTransform(scrollYProgress, [0, 1], IS_MOBILE ? ["0%", "0%"] : ["0%", "30%"]);
  const bgScale        = useTransform(scrollYProgress, [0, 1], IS_MOBILE ? [1, 1]        : [1.05, 1.15]);
  const contentY       = useTransform(scrollYProgress, [0, 1], IS_MOBILE ? ["0%", "0%"] : ["0%", "-12%"]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], IS_MOBILE ? [1, 1]        : [1, 0.4]);

  return (
    <section ref={heroRef} className="relative min-h-[92vh] flex items-center overflow-hidden" data-testid="amselweg-hero">
      <motion.div className="absolute inset-0 z-0" style={{ y: bgY, scale: bgScale }}>
        <picture>
          <source media="(min-width: 768px)" srcSet={HERO_SRC} />
          <img src={HERO_SRC_MOBILE} alt="Amselweg 14B — Potsdam-Marquardt newbuild" className="absolute inset-0 w-full h-full object-cover object-center opacity-70" loading="eager" fetchPriority="high" />
        </picture>
        <div className="absolute inset-0 bg-gradient-to-b from-ire-black/55 via-ire-black/60 to-ire-black" />
        <div className="absolute inset-0 bg-gradient-to-r from-ire-black/90 via-ire-black/40 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-transparent to-ire-accent/[0.05] mix-blend-overlay" />
      </motion.div>

      <motion.div style={{ opacity: overlayOpacity }} className="absolute top-1/4 right-0 w-[600px] h-[600px] bg-ire-accent/[0.08] rounded-full blur-[180px]" />

      <motion.div style={{ y: contentY }} className="relative z-10 max-w-[1800px] mx-auto px-4 sm:px-6 md:px-16 lg:px-24 py-24 sm:py-32 md:py-40 w-full">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }} className="max-w-4xl">
          <div className="flex items-center gap-3 mb-8">
            <div className="h-px w-12 bg-ire-accent" />
            <span className="font-body text-[11px] tracking-[0.4em] text-ire-accent uppercase">{T.eyebrow}</span>
          </div>
          <h1 className="font-heading text-[2.5rem] sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl text-white leading-[1] sm:leading-[0.95] mb-6 sm:mb-8">
            {T.title}<br />
            <span className="text-ire-accent italic font-light">{T.sub}</span>
          </h1>
          <p className="font-body text-base sm:text-lg lg:text-xl text-white/65 max-w-2xl leading-relaxed mb-10">
            {T.body}
          </p>
          <div className="flex flex-wrap items-center gap-8 text-xs">
            {T.chips.map((c, i) => (
              <div key={c.l} className="flex items-center gap-8">
                <div>
                  <div className="font-heading text-3xl text-ire-accent tabular-nums">{c.v}</div>
                  <div className="font-body text-[10px] tracking-[0.25em] text-white/40 uppercase mt-1">{c.l}</div>
                </div>
                {i < T.chips.length - 1 && <div className="h-10 w-px bg-white/10" />}
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.5, duration: 1 }} className="absolute bottom-12 left-6 sm:left-8 md:left-16 lg:left-24 flex items-center gap-3">
          <span className="font-body text-[10px] tracking-[0.4em] text-white/40 uppercase">{T.scroll}</span>
          <motion.div animate={{ y: [0, 6, 0] }} transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }} className="h-8 w-px bg-ire-accent/40" />
        </motion.div>
      </motion.div>
    </section>
  );
};

// ═════════════════════════════════════════════════════════
// ACT II — MACRO CASE · set the context, research, background
// ═════════════════════════════════════════════════════════
const MacroCase = () => {
  const T = useCopy().macro;
  return (
    <section className="relative py-20 md:py-40 px-4 sm:px-6 md:px-16 lg:px-24 border-t border-white/[0.04]" id="section-macro">
      <div className="max-w-[1800px] mx-auto">
        <Reveal>
          <div className="flex items-center gap-3 mb-6">
            <span className="font-body text-[11px] tracking-[0.4em] text-ire-accent uppercase">{T.act}</span>
            <div className="h-px w-12 bg-ire-accent/40" />
            <span className="font-body text-[11px] tracking-[0.4em] text-white/45 uppercase">{T.kicker}</span>
          </div>
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.05] mb-6 max-w-4xl">
            {T.headingA}<br />
            <span className="text-ire-accent italic">{T.headingB}</span>
          </h2>
          <p className="font-body text-base sm:text-lg text-white/65 max-w-3xl leading-relaxed mb-16">
            {T.body}
          </p>
        </Reveal>

        {/* Macro stat grid */}
        <Reveal delay={0.05}>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/[0.06] mb-16" data-testid="amselweg-macro-grid">
            {T.stats.map((s) => (
              <div key={s.l} className="bg-[#070707] p-7 md:p-9">
                <div className="font-heading text-3xl md:text-4xl text-ire-accent tabular-nums mb-2">{s.v}</div>
                <div className="font-body text-[11px] tracking-[0.2em] text-white/70 uppercase mb-1.5">{s.l}</div>
                <div className="font-body text-[11px] text-white/45 leading-snug">{s.s}</div>
              </div>
            ))}
          </div>
        </Reveal>

        {/* Three-layer thesis */}
        <Reveal delay={0.1}>
          <div className="grid lg:grid-cols-3 gap-6 mb-16">
            {T.layers.map(({ tag, icon: Icon, title, body }) => (
              <div key={tag} className="bg-[#050505] border border-white/[0.06] p-7 md:p-9">
                <div className="flex items-center gap-3 mb-5">
                  <Icon className="w-5 h-5 text-ire-accent" />
                  <div className="font-mono text-[10px] tracking-[0.3em] text-ire-accent uppercase">{tag}</div>
                </div>
                <h3 className="font-heading text-xl md:text-2xl text-white mb-3">{title}</h3>
                <p className="font-body text-sm text-white/65 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </Reveal>

        {/* Pull quote */}
        <Reveal delay={0.15}>
          <div className="border-l-2 border-ire-accent pl-6 md:pl-10 max-w-5xl">
            <p className="font-heading text-2xl md:text-3xl lg:text-4xl text-white/85 italic leading-[1.2]">
              {T.quoteA}<br className="hidden md:block" />
              <span className="text-ire-accent not-italic">{T.quoteB}</span>
            </p>
            <p className="font-body text-sm text-white/45 mt-6 max-w-2xl leading-relaxed">
              {T.quoteSub}
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

// Gallery tile — clickable, hover effect, expand icon. Defined outside AssetDetails to avoid unstable nested component.
const GalleryTile = ({ src, label, idx, onOpen, aspect = "aspect-[4/3]", priority = false }) => (
  <button
    type="button"
    onClick={() => onOpen(idx)}
    data-testid={`amselweg-tile-${idx}`}
    className={`relative ${aspect} overflow-hidden group block w-full cursor-zoom-in focus:outline-none focus-visible:ring-2 focus-visible:ring-ire-accent`}
  >
    <Img
      src={src}
      alt={label}
      sizes="(max-width: 640px) 100vw, (max-width: 1024px) 66vw, 50vw"
      loading={priority ? "eager" : "lazy"}
      decoding="async"
      fetchPriority={priority ? "high" : "auto"}
      className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
    />
    <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent opacity-90 group-hover:opacity-100 transition-opacity" />
    <div className="absolute top-3 right-3 w-9 h-9 bg-black/55 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 translate-y-[-4px] group-hover:translate-y-0 transition-all duration-300">
      <Maximize2 className="w-4 h-4 text-ire-accent" />
    </div>
    <div className="absolute bottom-3 left-4 right-4 font-mono text-[10px] tracking-[0.2em] uppercase text-white/90 text-left leading-snug">
      {label}
    </div>
  </button>
);

// ═════════════════════════════════════════════════════════
// ACT III — THE ASSET · gallery, snapshot, floor plan
// ═════════════════════════════════════════════════════════
const AssetDetails = () => {
  const T = useCopy().asset;
  // Curated gallery — order matters. First image is the hero anchor.
  const galleryImages = [
    { src: IMG.garden.thumb,        full: IMG.garden.full,        label: T.galleryLabels[0] },
    { src: IMG.kitchen.thumb,       full: IMG.kitchen.full,       label: T.galleryLabels[1] },
    { src: IMG.bath.thumb,          full: IMG.bath.full,          label: T.galleryLabels[2] },
    { src: IMG.bedroom.thumb,       full: IMG.bedroom.full,       label: T.galleryLabels[3] },
    { src: IMG.staircase.thumb,     full: IMG.staircase.full,     label: T.galleryLabels[4] },
    { src: IMG.exterior.thumb,      full: IMG.exterior.full,      label: T.galleryLabels[5] },
    { src: IMG.completedView.thumb, full: IMG.completedView.full, label: T.galleryLabels[6] },
    { src: IMG.streetView.thumb,    full: IMG.streetView.full,    label: T.galleryLabels[7] },
    { src: IMG.facade.thumb,        full: IMG.facade.full,        label: T.galleryLabels[8] },
    { src: IMG.kitchen2.thumb,      full: IMG.kitchen2.full,      label: T.galleryLabels[9] },
    { src: IMG.bathGreen.thumb,     full: IMG.bathGreen.full,     label: T.galleryLabels[10] },
    { src: IMG.bedroom2.thumb,      full: IMG.bedroom2.full,      label: T.galleryLabels[11] },
  ];
  const siteImages = [
    { src: IMG.siteOverview.thumb,  full: IMG.siteOverview.full,  label: T.siteLabels[0] },
    { src: IMG.construction1.thumb, full: IMG.construction1.full, label: T.siteLabels[1] },
    { src: IMG.streetscape.thumb,   full: IMG.streetscape.full,   label: T.siteLabels[2] },
    { src: IMG.construction4.thumb, full: IMG.construction4.full, label: T.siteLabels[3] },
  ];

  const galleryLB = useLightbox(galleryImages);
  const siteLB = useLightbox(siteImages);

  const trackRecord = [
    { id: "01", status: T.letLabel,   tone: "done" },
    { id: "02", status: T.letLabel,   tone: "done" },
    { id: "03", status: T.letLabel,   tone: "done" },
    { id: "04", status: T.letLabel,   tone: "done" },
    { id: "05", status: T.soldLabel,  tone: "done" },
    { id: "06", status: T.availLabel, tone: "open" },
  ];

  return (
    <section className="relative py-20 md:py-40 px-4 sm:px-6 md:px-16 lg:px-24 border-t border-white/[0.04]" id="section-asset">
      <Lightbox images={galleryImages} {...galleryLB} />
      <Lightbox images={siteImages} {...siteLB} />
      <div className="max-w-[1800px] mx-auto">
        <Reveal>
          <div className="flex items-center gap-3 mb-6">
            <span className="font-body text-[11px] tracking-[0.4em] text-ire-accent uppercase">{T.act}</span>
            <div className="h-px w-12 bg-ire-accent/40" />
            <span className="font-body text-[11px] tracking-[0.4em] text-white/45 uppercase">{T.kicker}</span>
          </div>
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.05] mb-6 max-w-4xl">
            {T.headingA}<br />
            <span className="text-ire-accent italic">{T.headingB}</span>
          </h2>
          <p className="font-body text-base sm:text-lg text-white/65 max-w-3xl leading-relaxed mb-12">
            {T.body}
          </p>
        </Reveal>

        {/* Track-record band */}
        <Reveal delay={0.05}>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-px bg-white/[0.06] mb-16" data-testid="amselweg-track-record">
            {trackRecord.map((u) => (
              <div key={u.id} className={`p-5 ${u.tone === "open" ? "bg-ire-accent/[0.08] border border-ire-accent/40 shadow-[0_0_40px_-15px_rgba(212,175,106,0.5)]" : "bg-[#070707]"}`}>
                <div className={`font-mono text-[10px] tracking-[0.25em] uppercase mb-2 ${u.tone === "open" ? "text-ire-accent" : "text-white/40"}`}>● {T.unitLbl} {u.id}</div>
                <div className={`font-body text-xs ${u.tone === "open" ? "text-ire-accent font-medium" : "text-emerald-400/80"}`}>{u.status}</div>
              </div>
            ))}
          </div>
        </Reveal>

        {/* Editorial gallery */}
        <div className="grid lg:grid-cols-12 gap-6 lg:gap-8 mb-12">
          {/* Gallery: 8 columns */}
          <div className="lg:col-span-8 space-y-6">
            <Reveal>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="amselweg-gallery">
                <div className="md:col-span-2 md:row-span-2 aspect-[16/12]">
                  <GalleryTile src={galleryImages[0].src} label={galleryImages[0].label} idx={0} onOpen={galleryLB.open} aspect="h-full aspect-auto" priority />
                </div>
                {[1, 2, 3, 4].map((i) => (
                  <GalleryTile key={i} src={galleryImages[i].src} label={galleryImages[i].label} idx={i} onOpen={galleryLB.open} aspect="aspect-[5/4]" />
                ))}
              </div>
            </Reveal>
            <Reveal delay={0.05}>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[5, 6, 7, 8].map((i) => (
                  <GalleryTile key={i} src={galleryImages[i].src} label={galleryImages[i].label} idx={i} onOpen={galleryLB.open} aspect="aspect-[4/3]" />
                ))}
              </div>
            </Reveal>
          </div>

          {/* Snapshot rail: 4 columns */}
          <div className="lg:col-span-4 space-y-6">
            <Reveal delay={0.05}>
              <div className="bg-[#0a0a0a] border border-ire-accent/20 shadow-[0_0_60px_-25px_rgba(212,175,106,0.35)] sticky top-24" data-testid="amselweg-snapshot">
                <div className="px-6 md:px-7 pt-6 md:pt-7 pb-4 border-b border-white/[0.06]">
                  <div className="font-body text-[10px] tracking-[0.3em] text-ire-accent uppercase mb-2">{T.dealSnapshotKicker}</div>
                  <h3 className="font-heading text-2xl text-white">{T.dealSnapshotHeading}</h3>
                </div>
                <div>
                  {T.snapshotRows.map((row) => (
                    <div key={row.l} className={`flex items-baseline justify-between gap-3 px-6 md:px-7 py-3 border-b border-white/[0.04] last:border-b-0 ${row.emphasis ? "bg-ire-accent/[0.05]" : ""}`}>
                      <span className="font-body text-[10px] tracking-[0.18em] uppercase text-white/55 shrink-0">{row.l}</span>
                      <span className={`font-mono text-xs tabular-nums text-right ${row.emphasis ? "text-ire-accent" : "text-white/90"}`}>{row.v}</span>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              <div className="bg-black/30 border border-white/[0.07] p-6">
                <div className="font-body text-[10px] tracking-[0.3em] text-ire-accent uppercase mb-4">{T.specHighlights}</div>
                <ul className="space-y-2.5">
                  {T.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 font-body text-sm text-white/75">
                      <CheckCircle2 className="w-4 h-4 text-ire-accent mt-0.5 shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>

        {/* Construction proof strip */}
        <Reveal delay={0.15}>
          <div className="border-t border-white/[0.06] pt-10">
            <div className="flex items-baseline justify-between mb-5">
              <div className="font-body text-[10px] tracking-[0.3em] text-ire-accent uppercase">{T.siteProofKicker}</div>
              <div className="font-body text-[10px] tracking-[0.2em] text-white/35 uppercase">{T.clickExpand}</div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="amselweg-site-proof">
              {siteImages.map((p, i) => (
                <button
                  type="button"
                  key={p.label}
                  onClick={() => siteLB.open(i)}
                  data-testid={`amselweg-site-${i}`}
                  className="relative aspect-[4/3] overflow-hidden group cursor-zoom-in focus:outline-none focus-visible:ring-2 focus-visible:ring-ire-accent"
                >
                  <Img src={p.src} alt={p.label} sizes="(max-width: 640px) 50vw, 25vw" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent" />
                  <div className="absolute top-3 right-3 w-8 h-8 bg-black/55 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all">
                    <Maximize2 className="w-3.5 h-3.5 text-ire-accent" />
                  </div>
                  <div className="absolute bottom-2.5 left-3 font-mono text-[9px] tracking-[0.25em] uppercase text-white/85">{p.label}</div>
                </button>
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

// ═════════════════════════════════════════════════════════
// ACT IV — Why-Not-Normal frame shift
// ═════════════════════════════════════════════════════════
const FrameShift = () => {
  const T = useCopy().frame;
  return (
    <section className="relative py-20 md:py-32 px-4 sm:px-6 md:px-16 lg:px-24 border-t border-white/[0.04] overflow-hidden">
      <div className="absolute inset-0 opacity-20">
        <img src={IMG.completedView.thumb} alt="" className="w-full h-full object-cover" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-b from-ire-black via-ire-black/90 to-ire-black" />
      </div>
      <div className="relative max-w-[1800px] mx-auto">
        <Reveal>
          <div className="flex items-center gap-3 mb-6">
            <span className="font-body text-[11px] tracking-[0.4em] text-ire-accent uppercase">{T.kicker}</span>
            <div className="h-px w-12 bg-ire-accent/40" />
            <span className="font-body text-[11px] tracking-[0.4em] text-white/45 uppercase">{T.side}</span>
          </div>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-white leading-[1.08] mb-12 max-w-4xl">
            {T.headingA}<br />
            <span className="text-ire-accent italic">{T.headingB}</span>
          </h2>
        </Reveal>
        <div className="grid md:grid-cols-3 gap-6">
          {T.cards.map(({ icon: Icon, h, p }) => (
            <Reveal key={h}>
              <div className="bg-black/60 backdrop-blur-sm border border-white/[0.07] p-7 md:p-8 h-full hover:border-ire-accent/40 transition-colors duration-500">
                <Icon className="w-6 h-6 text-ire-accent mb-5" />
                <h3 className="font-heading text-lg md:text-xl text-white mb-3">{h}</h3>
                <p className="font-body text-sm text-white/65 leading-relaxed">{p}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

// ═════════════════════════════════════════════════════════
// CLOSING CTA — project-specific
// ═════════════════════════════════════════════════════════
const ClosingCTA = () => {
  const navigate = useNavigate();
  const T = useCopy().closing;

  return (
    <section className="relative py-20 md:py-40 px-4 sm:px-6 md:px-16 lg:px-24 border-t border-white/[0.04] overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a] via-[#080808] to-ire-black" />
      <div className="absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-ire-accent/[0.04] to-transparent" />
      <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-ire-accent/[0.05] rounded-full blur-[150px]" />
      <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-ire-accent/[0.04] rounded-full blur-[180px]" />
      <div className="relative z-10 max-w-[1800px] mx-auto">
        <Reveal>
          <div className="flex items-center gap-3 mb-6">
            <div className="h-px w-12 bg-ire-accent" />
            <span className="font-body text-[11px] tracking-[0.4em] text-ire-accent uppercase">{T.kicker}</span>
          </div>
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.05] mb-8 max-w-4xl">
            {T.headingA}<br />
            <span className="text-ire-accent italic">{T.headingB}</span>
          </h2>
          <p className="font-body text-base sm:text-lg text-white/65 max-w-3xl leading-relaxed mb-16">
            {T.body}
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="grid md:grid-cols-3 gap-px bg-white/[0.06] mb-16">
            {T.steps.map((s) => (
              <div key={s.n} className="bg-[#050505] p-8 md:p-10">
                <div className="flex items-baseline justify-between mb-6">
                  <span className="font-mono text-3xl text-ire-accent tabular-nums">{s.n}</span>
                  <div className="h-px w-12 bg-ire-accent/30" />
                </div>
                <h3 className="font-heading text-xl md:text-2xl text-white mb-4">{s.title}</h3>
                <p className="font-body text-sm text-white/55 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </Reveal>
        <Reveal delay={0.2}>
          <div className="flex flex-col items-center gap-6 text-center">
            <button onClick={openCalendly} data-testid="amselweg-closing-cta-call" className="font-body text-xs tracking-[0.2em] uppercase text-black bg-ire-accent px-10 py-4 hover:bg-ire-accentHover transition-all flex items-center gap-3 group">
              {T.primaryCta}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
            <button onClick={() => navigate("/apply")} data-testid="amselweg-apply-cta" className="font-body text-[10px] tracking-[0.3em] uppercase text-white/55 hover:text-ire-accent transition-colors">
              {T.secondaryCta}
            </button>
            <div className="flex flex-wrap items-center gap-4 text-xs text-white/50 mt-2">
              <a href="mailto:info@ireberlin.com" className="hover:text-ire-accent transition-colors">info@ireberlin.com</a>
              <span className="text-white/20">·</span>
              <a href="tel:+491621871064" className="hover:text-ire-accent transition-colors">+49 162 1871064</a>
            </div>
            <p className="font-body text-[11px] text-white/30 leading-relaxed max-w-2xl mt-6">
              {T.disclaimer}
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

// ═════════════════════════════════════════════════════════
// MAIN — Lenis-smoothed, full-width dealroom
// ═════════════════════════════════════════════════════════
const AmselwegPage = () => {
  const CT = useCopy().calc;
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  // Lenis smooth scroll — DESKTOP ONLY (mobile uses native momentum).
  useEffect(() => {
    const isMobile = matchMedia('(hover: none) and (pointer: coarse)').matches
      || /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);
    if (isMobile) return;

    const lenis = new Lenis({
      duration: 1.6,
      easing: (t) => 1 - Math.pow(1 - t, 4),
      smoothWheel: true,
      wheelMultiplier: 0.85,
      touchMultiplier: 1.6,
      lerp: 0.08,
    });
    window.__lenis = lenis;
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <div className="relative bg-ire-black text-white" data-testid="amselweg-page">
      <HeroPreload src="/assets/amselweg/img-01.jpg" sizes="100vw" />
      <ScrollProgressHUD />
      <AmbientCursorGlow />
      <Hero />
      <MacroCase />
      <AssetDetails />
      <FrameShift />
      <section id="section-faq"><FaqObjections /></section>
      <ClosingCTA />
    </div>
  );
};

export default AmselwegPage;
